import { useState } from "react";
import { Bell, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * Compact "Notify when back in stock" form.
 * - product: full product object
 * - size: optional variant size string (null for non-sized products)
 */
export default function NotifyBackInStock({ product, size }) {
  const { user } = useAuth();
  const [email, setEmail] = useState(user?.email || "");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !trimmed.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/products/${product.id}/notify-restock`, {
        email: trimmed,
        variant_size: size || null,
      });
      setDone(true);
      toast.success(data.already_subscribed
        ? "You're already on the list — we'll text email you 💌"
        : "You're in! We'll email you the moment it's back ✨");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't subscribe");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="notify-restock-done"
        className="mt-5 flex items-start gap-3 bg-[#2eb6a8]/10 border-2 border-[#2eb6a8]/40 rounded-2xl px-4 py-3.5"
      >
        <Check size={18} className="text-[#1f8d83] mt-0.5 flex-shrink-0"/>
        <div className="text-sm leading-snug">
          <div className="font-bold uppercase tracking-[0.18em] text-[#1f8d83] text-[11px]">You're On The List</div>
          <div className="text-[#1c1815] mt-0.5">We'll email <strong>{email}</strong> the moment {size ? `size ${size}` : "this"} is back.</div>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="notify-restock-form"
      className="mt-5 bg-[#c93875]/8 border-2 border-[#c93875]/40 rounded-2xl px-4 py-3.5"
    >
      <div className="flex items-center gap-2 mb-2">
        <Bell size={16} className="text-[#c93875]"/>
        <div className="font-bold uppercase tracking-[0.18em] text-[#c93875] text-[11px]">
          Sold Out{size ? ` — Size ${size}` : ""}
        </div>
      </div>
      <div className="text-sm text-[#1c1815] mb-3">Get an email the second it's back in stock.</div>
      <div className="flex gap-2">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          data-testid="notify-restock-email"
          className="flex-1 bg-white border border-[#e5dccd] focus:border-[#c93875] focus:ring-2 focus:ring-[#c93875]/20 rounded-full px-4 py-2.5 text-sm outline-none text-[#1c1815]"
        />
        <button
          type="submit"
          disabled={submitting}
          data-testid="notify-restock-submit"
          className="bg-[#c93875] hover:bg-[#e8478e] text-white font-bold uppercase tracking-[0.18em] text-[11px] px-4 py-2.5 rounded-full transition-all disabled:opacity-60 whitespace-nowrap"
        >
          {submitting ? "…" : "Notify Me"}
        </button>
      </div>
    </form>
  );
}
